/**
 * FeatureErrorFallback Component
 *
 * Compact inline error fallback for feature-level error boundaries.
 * Designed for isolating errors in specific features like Schedule or Forms
 * without affecting the rest of the page.
 *
 * Features:
 * - Compact, inline design
 * - Shows error icon and message
 * - Retry button to reset the error boundary
 * - Does not take over the entire page
 *
 * @example
 * ```tsx
 * <ErrorBoundary FallbackComponent={FeatureErrorFallback}>
 *   <Schedule />
 * </ErrorBoundary>
 * ```
 */

import type { FC } from "react";
import { Button } from "@/components/ui/buttons/Button";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { FeatureErrorFallbackProps } from "./types";
import styles from "./FeatureErrorFallback.module.css";

export const FeatureErrorFallback: FC<FeatureErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  title = "Noe gikk galt",
  message,
  retryButtonText = "Prøv igjen",
  className = "",
  "data-testid": testId = "feature-error-fallback",
}) => {
  // Use custom message or derive from error in dev
  const errorMessage =
    message ||
    (import.meta.env.DEV
      ? error.message
      : "En feil oppstod. Vennligst prøv igjen.");

  const containerClasses = cx(styles.container, className);

  return (
    <div className={containerClasses} role="alert" data-testid={testId}>
      <div className={styles.content}>
        {/* Error icon */}
        <div className={styles.iconWrapper}>
          <Icon name="error" size="lg" className={styles.icon} aria-hidden />
        </div>

        {/* Error text */}
        <div className={styles.textContent}>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.message}>{errorMessage}</p>
        </div>

        {/* Retry button */}
        <Button
          variant="primary"
          size="sm"
          onClick={resetErrorBoundary}
          className={styles.retryButton}
          data-testid={`${testId}-retry-button`}
        >
          <Icon name="rotate-right" size="sm" aria-hidden />
          {retryButtonText}
        </Button>
      </div>

      {/* Dev-only error details */}
      {import.meta.env.DEV && (
        <details
          className={styles.errorDetails}
          data-testid={`${testId}-details`}
        >
          <summary>Tekniske detaljer</summary>
          <pre className={styles.errorStack}>{error.stack}</pre>
        </details>
      )}
    </div>
  );
};

FeatureErrorFallback.displayName = "FeatureErrorFallback";
